import { useCallback } from "react";

import { Box, Typography } from "@mui/material";
import PropTypes from "prop-types";

import { apiOutletClient } from "@/features/shared";
import { CustomAutocomplete } from "@/features/ui";
import { chartOfAccountTypeLabels } from "@/features/chart-of-accounts";

export const OutletParentChartOfAccountSelect = ({
  outletId,
  accountType = null,
  excludeSubtreeId = null,
  ...props
}) => {
  const fetchOutletChartOfAccounts = useCallback(async () => {
    if (!outletId) return [];

    const params = {};

    // For UPDATE scenario: exclude the node and all its descendants
    if (excludeSubtreeId) {
      params.id = excludeSubtreeId;
    }

    // For CREATE scenario: pass account type to filter parents
    if (accountType) {
      params.type = accountType;
    }

    const {
      data: { data },
    } = await apiOutletClient.get("chart-of-accounts/allowed-parents", {
      params,
      headers: {
        "Outlet-Id": outletId,
      },
    });

    return data;
  }, [outletId, excludeSubtreeId, accountType]);

  return (
    <CustomAutocomplete
      fetchOptions={fetchOutletChartOfAccounts}
      getOptionLabel={(option) => `${option.code} - ${option.name}`}
      renderOption={(props, option) => {
        // eslint-disable-next-line react/prop-types
        const { key, ...otherProps } = props;
        return (
          <Box component="li" key={key} {...otherProps}>
            <Box>
              <Typography variant="body2">
                {option.code} - {option.name}
              </Typography>
              <Typography variant="caption" sx={{
                color: "text.secondary"
              }}>
                {chartOfAccountTypeLabels[option.type]}
              </Typography>
            </Box>
          </Box>
        );
      }}
      isOptionEqualToValue={(option, selectedOption) =>
        option.id === selectedOption.id
      }
      {...props}
    />
  );
};

OutletParentChartOfAccountSelect.propTypes = {
  outletId: PropTypes.string.isRequired,
  accountType: PropTypes.string,
  excludeSubtreeId: PropTypes.string,
};
